import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Trash2, Undo2, AlertTriangle } from "lucide-react";
import { photoUrl } from "@/api";
import { fmtTime } from "@/lib/constants";

export function Lightbox({ photos, startIndex = 0, onClose, token, canFlagDelete, onFlagDelete, canModerate, onApproveDelete, onRejectDelete }) {
  const [i, setI] = useState(startIndex);
  const n = Array.isArray(photos) ? photos.length : 0;

  useEffect(() => { setI(startIndex); }, [startIndex]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setI((v) => (v - 1 + n) % n);
      else if (e.key === "ArrowRight") setI((v) => (v + 1) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [n, onClose]);

  if (!n) return null;
  const p = photos[Math.max(0, Math.min(i, n - 1))];
  const prev = (e) => { e.stopPropagation(); setI((v) => (v - 1 + n) % n); };
  const next = (e) => { e.stopPropagation(); setI((v) => (v + 1) % n); };
  const flagged = !!p.delete_requested;

  return (
    <div data-testid="lightbox" className="fixed inset-0 z-[999] flex items-center justify-center bg-black/85 p-6" onClick={onClose}>
      <button data-testid="lightbox-close" onClick={onClose} className="absolute right-5 top-5 rounded-lg bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
      {n > 1 && (
        <button data-testid="lightbox-prev" onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>
      )}
      {n > 1 && (
        <button data-testid="lightbox-next" onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>
      )}
      <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-4xl text-center">
        <img src={photoUrl(p.id, token)} alt={p.original_filename || ""} className="mx-auto max-h-[74vh] rounded-xl" />
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-300">
          {p.source === "live_snapshot" && <span className="rounded bg-red-600 px-1.5 py-0.5 font-semibold text-white">LIVE-SNAPSHOT</span>}
          <span>{p.original_filename}{p.uploaded_by ? ` · ${p.uploaded_by}` : ""}{p.created_at ? ` · ${fmtTime(p.created_at)}` : ""}{n > 1 ? ` · ${i + 1}/${n}` : ""}</span>
        </div>
        {flagged && (
          <div data-testid="lightbox-flag-notice" className="mx-auto mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-500/20 px-3 py-1.5 text-xs font-semibold text-amber-200">
            <AlertTriangle className="h-3.5 w-3.5" /> Verwijdering aangevraagd door {p.delete_requested.by}{p.delete_requested.reason ? ` — ${p.delete_requested.reason}` : ""}
          </div>
        )}
        {(canFlagDelete || canModerate) && (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            {canFlagDelete && (
              flagged ? (
                <span data-testid="lightbox-flag-done" className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-amber-200">Verzoek ingediend</span>
              ) : (
                <button data-testid="lightbox-flag-btn" onClick={() => onFlagDelete && onFlagDelete(p)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-400">
                  <Trash2 className="h-3.5 w-3.5" /> Markeer voor verwijdering
                </button>
              )
            )}
            {canModerate && (
              <>
                <button data-testid="lightbox-approve-delete" onClick={() => onApproveDelete && onApproveDelete(p)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500">
                  <Trash2 className="h-3.5 w-3.5" /> {flagged ? "Verwijderen (goedkeuren)" : "Verwijderen"}
                </button>
                {flagged && (
                  <button data-testid="lightbox-reject-delete" onClick={() => onRejectDelete && onRejectDelete(p)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/20">
                    <Undo2 className="h-3.5 w-3.5" /> Behouden (afwijzen)
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
